import {keyToType, sItemIntToKey} from '../../../shared-utilities/models-old/datastructures';
import {Timestamp} from '@angular/fire/firestore';


export function convertObjectToJSON<ObjectType>(obj: ObjectType): string {
  return JSON.stringify(obj);
}

export function convertJSONtoObject<ObjectType>(json: string): ObjectType {
  return JSON.parse(json) as ObjectType;
}

export function isNonEmptyObject<ObjectType>(obj: ObjectType | null | undefined): boolean {
  return !!obj && Object.keys(obj).length > 0;
}

/**
 * Creates a deep copy of an object, ensuring nested objects are also copied.
 *
 * This function uses `JSON.parse` and `JSON.stringify` to perform a deep copy of the input object.
 * If the object is `undefined` or `null`, it simply returns the same value.
 *
 * @param {ObjectType | undefined | null} obj - The object to be copied.
 * @returns {ObjectType | undefined | null} A deep copy of the input object or the original value if `undefined` or `null`.
 *
 * @template ObjectType - The type of the object to be copied.
 */
export function getDeepCopyOfObject<ObjectType>(
  obj: ObjectType | undefined | null,
): ObjectType | undefined | null {
  if (obj === undefined || obj === null) {
    return obj;
  }
  return JSON.parse(JSON.stringify(obj)) as ObjectType;
}

/**
 * Creates a shallow copy of an object, copying only the top-level properties.
 *
 * This function uses object spread (`{ ...obj }`) to create a shallow copy.
 * If the object is `undefined` or `null`, it simply returns the same value.
 *
 * @param {ObjectType | undefined | null} obj - The object to be copied.
 * @returns {ObjectType | undefined | null} A shallow copy of the input object or the original value if `undefined` or `null`.
 *
 * @template ObjectType - The type of the object to be copied.
 */
export function getShallowCopyOfObject<ObjectType>(
  obj: ObjectType | undefined | null,
): ObjectType | undefined | null {
  if (obj === undefined || obj === null) {
    return obj;
  }
  return {...obj};
}

/**
 * Function to cast an object to a specified type.
 *
 * @template CastType - The type to which the object should be cast.
 * @template ObjectType - The type of the input object.
 *
 * @param {ObjectType} object - The object to be cast to the specified type.
 * @returns {CastType} - The object cast to the specified type.
 */
export function castObjectToType<CastType, ObjectType>(object: ObjectType): CastType {
  const newObject: CastType = {} as CastType;
  Object.keys(object).forEach((key: string): void => {
    if (key === 'objectID' || sItemIntToKey[key] === 'stockId') {
      newObject[sItemIntToKey[key]] = decodeURIComponent(object[key] as string);
    } else if (keyToType[sItemIntToKey[key]] === 'date' && object[key]) {
      newObject[sItemIntToKey[key]] = (object[key] as Timestamp).toDate();
    } else {
      newObject[sItemIntToKey[key]] = object[key];
    }
  });
  return newObject;
}

/**
 * Creates a map from an object where the values are objects of a generic type,
 * and each value is a specific property of that object.
 *
 * @template ObjectType - The type of the objects within the input object.
 *
 * @param {{ [p: string]: ObjectType }} suppliers - An object where the keys are strings and the values are of type `ObjectType`.
 * @param {string} property - The specific property to map from each object.
 *
 * @returns {{ [key: string]: any }} A map where the keys are the same as the input object, but the values are the specific property from each object.
 */
export function createMapFromObjectsObject<ObjectType>(suppliers: Record<string, ObjectType>, property: string): Record<string, ObjectType> {
  const supplierMap: Record<string, ObjectType> = {};
  for (const key in suppliers) {
    if (suppliers.hasOwnProperty(key)) {
      supplierMap[key] = suppliers[key][property];
    }
  }
  return supplierMap;
}
